import { DocumentType } from "@dune/types";
import {
	Button,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@dune/ui";
import { useFormContext } from "react-hook-form";
import { useGetFileWithMetadata } from "../../../../api/queries/files.queries";
import { useDocumentStore } from "../../../../stores/document.store";
import { FileStatusChip } from "../../../common/files/FileStatusChip";
import { AddDocumentButton } from "../AddDocumentButton";

type FilesFieldsNames =
	| "decennialCertificatesFilesIds"
	| "feeAgreementsFilesIds"
	| "quotesFilesIds"
	| "quoteFilesIds";

interface ParticipantDocumentsTableProps {
	constructionSiteId: number;
	participantType: "DESIGNER" | "BUILDER" | "MATERIAL";
}

export const ParticipantDocumentsTable = ({
	constructionSiteId,
	participantType,
}: ParticipantDocumentsTableProps) => {
	const documentTypes = getDocumentTypesForParticipant(participantType);

	const { setValue, watch } = useFormContext();

	return (
		<div className="space-y-4">
			<h3 className="text-lg font-medium">Pièces justificatives</h3>
			<div className="rounded-lg border border-gray-200">
				<Table className="table-fixed w-full">
					<TableHeader>
						<TableRow>
							<TableHead>Type</TableHead>
							<TableHead>Fichier(s)</TableHead>
							<TableHead>Statut</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{documentTypes.map((documentType) => (
							<TableRow key={documentType.fieldName}>
								<TableCell className="truncate">{documentType.label}</TableCell>
								<TableCell className="truncate align-top">
									<DocumentFiles
										fieldName={documentType.fieldName}
										participantType={participantType}
									/>
								</TableCell>
								<TableCell className="truncate align-top">
									<div className="flex flex-col gap-2">
										<DocumentFilesStatus
											fieldName={documentType.fieldName}
											participantType={participantType}
										/>
										<div>
											<AddDocumentButton
												constructionSiteId={constructionSiteId}
												onUploadSuccess={(fileId) => {
													setValue(documentType.fieldName, [
														...(watch(documentType.fieldName) || []),
														fileId,
													]);
												}}
											/>
										</div>
									</div>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</div>
	);
};

const DocumentFiles = ({
	fieldName,
}: {
	fieldName: string;
	participantType: "DESIGNER" | "BUILDER" | "MATERIAL";
}) => {
	const { watch } = useFormContext();
	const fileIds: string[] = watch(fieldName) || [];
	const selectDocument = useDocumentStore((state) => state.selectDocument);

	const handleFileClick = (fileId: string) => {
		selectDocument("", fileId, "", "");
	};

	return (
		<div className="flex flex-col gap-2">
			{fileIds.map((fileId: string) => (
				<DocumentFileItem
					key={fileId}
					fileId={fileId}
					onClick={() => handleFileClick(fileId)}
				/>
			))}
		</div>
	);
};

const DocumentFileItem = ({
	fileId,
	onClick,
}: {
	fileId: string;
	onClick: () => void;
}) => {
	const { data: fileData, isLoading } = useGetFileWithMetadata(fileId);

	if (isLoading) {
		return (
			<div className="animate-pulse">
				<div className="h-4 bg-gray-200 rounded w-3/4" />
			</div>
		);
	}

	if (!fileData) {
		return (
			<span className="text-sm text-gray-500">
				Fichier non trouvé ({fileId})
			</span>
		);
	}

	const handleClick = (event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		onClick();
	};

	return (
		<Button
			type="button"
			variant="link"
			size="sm"
			onClick={handleClick}
			className="p-0 py-1 h-auto font-normal text-primary text-left justify-start"
		>
			{fileData.name}
		</Button>
	);
};

const DocumentFilesStatus = ({
	fieldName,
}: {
	fieldName: string;
	participantType: "DESIGNER" | "BUILDER" | "MATERIAL";
}) => {
	const { watch } = useFormContext();
	const fileIds: string[] = watch(fieldName) || [];

	return (
		<div className="flex flex-col gap-2">
			{fileIds.map((fileId: string) => (
				<div key={fileId}>
					<FileStatusChip humanProcessingStatus="TO_BE_PROCESSED" />
				</div>
			))}
		</div>
	);
};

const getDocumentTypesForParticipant = (
	participantType: "DESIGNER" | "BUILDER" | "MATERIAL",
): {
	fieldName: FilesFieldsNames;
	label: string;
	documentType: DocumentType;
}[] => {
	switch (participantType) {
		case "DESIGNER":
			return [
				{
					fieldName: "feeAgreementsFilesIds",
					label: "Convention d'honoraires",
					documentType: "CONVENTION_HONORAIRES",
				},
				{
					fieldName: "decennialCertificatesFilesIds",
					label: "Attestation décennale",
					documentType: "ATTESTATION_DECENNALE",
				},
			];
		case "BUILDER":
			return [
				{
					fieldName: "quotesFilesIds",
					label: "Devis",
					documentType: "DEVIS",
				},
				{
					fieldName: "decennialCertificatesFilesIds",
					label: "Attestation décennale",
					documentType: "ATTESTATION_DECENNALE",
				},
			];
		case "MATERIAL":
			return [
				{
					fieldName: "quoteFilesIds",
					label: "Facture",
					documentType: "FACTURE",
				},
			];
		default:
			return [];
	}
};
